import React from "react";
import "./Participants.css";

import { RedArrowSVG } from "../../../constants/svgs";
import { useI18n } from "../../../i18n/i18n";

import main_characters from "../../../assets/images/webp/main_characters.webp";
import alvaro from "../../../assets/images/webp/characters/alvaro.webp";
import cristina from "../../../assets/images/webp/characters/christina.webp";
import diana from "../../../assets/images/webp/characters/diana.webp";
import jaime from "../../../assets/images/webp/characters/jaime.webp";
import lucas from "../../../assets/images/webp/characters/lucas.webp";
import manuel from "../../../assets/images/webp/characters/manuel.webp";
import octavio from "../../../assets/images/webp/characters/octavio.webp";
import ximena from "../../../assets/images/webp/characters/ximena.webp";

import { dict } from "../../../i18n/dict";

type MemberId = (typeof dict.home.participants.members)[number]["id"];

const Participants: React.FC = () => {
    const { lang } = useI18n();

    // layout + assets stay here (no i18n)
    const members = [
        {
            id: 1 as MemberId,
            infoX: 16,
            hotspotX: 14,
            hotspotY: 72,
            overlay: lucas,
        },
        {
            id: 2 as MemberId,
            infoX: 24,
            hotspotX: 22,
            hotspotY: 72,
            overlay: alvaro,
        },
        {
            id: 3 as MemberId,
            infoX: 36,
            hotspotX: 34,
            hotspotY: 72,
            overlay: ximena,
        },
        {
            id: 4 as MemberId,
            infoX: 46,
            hotspotX: 44,
            hotspotY: 72,
            overlay: jaime,
        },
        {
            id: 5 as MemberId,
            infoX: 56,
            hotspotX: 54,
            hotspotY: 72,
            overlay: manuel,
        },
        {
            id: 6 as MemberId,
            infoX: 62,
            hotspotX: 60,
            hotspotY: 72,
            overlay: octavio,
        },
        {
            id: 7 as MemberId,
            infoX: 76,
            hotspotX: 74,
            hotspotY: 72,
            overlay: cristina,
        },
        {
            id: 8 as MemberId,
            infoX: 82,
            hotspotX: 80,
            hotspotY: 72,
            overlay: diana,
        },
    ] as const;

    const getMemberCopy = (id: MemberId) =>
        dict.home.participants.members.find((m) => m.id === id) ?? null;

    const [activeId, setActiveId] = React.useState<MemberId | null>(null);

    const activeMember = activeId
        ? members.find((m) => m.id === activeId) || null
        : null;

    const activeCopy = activeMember ? getMemberCopy(activeMember.id) : null;

    return (
        <div className="scene">
            {/* Base BW image */}
            <img className="scene-base" src={main_characters} alt="" />

            {/* Color overlays */}
            {members.map((m) => {
                const copy = getMemberCopy(m.id);
                const alt = copy ? copy.name[lang] : "";
                return (
                    <img
                        key={m.id}
                        src={m.overlay}
                        alt={alt}
                        className={"scene-overlay" + (activeId === m.id ? " is-visible" : "")}
                    />
                );
            })}

            {/* Invisible hotspots */}
            {members.map((m) => (
                <div
                    key={m.id}
                    className="scene-hotspot"
                    style={{ left: `${m.hotspotX}%`, top: `${m.hotspotY}%` }}
                    onMouseEnter={() => setActiveId(m.id)}
                    onMouseLeave={() => setActiveId(null)}
                />
            ))}

            {/* Floating info */}
            {activeMember && activeCopy && (
                <div
                    className="scene-info"
                    style={{ "--info-x": `${activeMember.infoX}%` } as React.CSSProperties}
                >
                    <h2 className="scene-info-name">{activeCopy.name[lang]}</h2>
                    <p className="scene-info-description">{activeCopy.description[lang]}</p>
                    <div className="scene-info-arrow">
                        <RedArrowSVG />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Participants;
